import Vue from "vue";
import Vuex from "vuex";
import {
  getCustomerInfoApi,
  getCustomerInfoByExternalUserId,
  getBrandConfigApi,
  wxWorkGetUserInfoApi,
  wxWorkSwitchUserInfoApi,
  getEnums,
} from "@/api";
import { useDefaultWeChatConfig, isPureWeChat, isWxWork } from "@/utils";
import config from "@conf";
import { Toast } from "vant";
import { USER_ROLES, ROLES } from "@/consts";
import createPersistedState from "vuex-persistedstate";

import brandLogo from "@/assets/mock-images/logo.header.png";
import brandImage from "@/assets/mock-images/background-banner.png";

Vue.use(Vuex);
const store = new Vuex.Store({
  plugins: [
    createPersistedState({
      paths: ["brandInfo", "account", "user", "customerInfo", "customerDetail"],
    }),
  ],
  state: {
    readyState: 0,
    brandInfo: { logo: brandLogo, image: brandImage },
    account: {
      signed: false,
      role: ROLES.GUEST, // role 不一定完全等于 user.caPosition, STORE_MANAGER 可以兼职 SA 用来给客户下单
    },
    user: {},
    // currentStore: null,
    customerInfo: {
      customerExternalUserid: "",
      salesWwid: "",
      name: "",
      mobile: "",
      headImg: "",
    },
    customerDetail: {
      customerExternalUserid: "",
      customerGender: 0,
      customerNickname: "",
      customerAvatar: "",
      lastOrderTime: "",
      totalPrice: 0,
    },
    logisticsOrderDetail: {},
  },
  getters: {
    // SA Before After
    chooseDeliveryType(state) {
      return (
        state.brandInfo.splitPayment?.enabled === true &&
        state.brandInfo.splitPayment?.type
      );
    },
    isReady(state) {
      return state.readyState >= 2;
    },
    runInWxWork() {
      return isWxWork || config.isDevelopAsInWxWork;
    },
    runInWeChat() {
      return isPureWeChat || config.isDevelopAsInWeChat;
    },
    currentStore({ account: { signed }, user: { currentStore } }) {
      return (signed && currentStore) || null;
    },
    canCreateOrder({ account: { signed }, user: { caPosition } }) {
      return (
        signed &&
        (caPosition === USER_ROLES.CLIENT_AGENT ||
          caPosition === USER_ROLES.STORE_MANAGER)
      );
    },
    isMultiStores({ account: { signed }, user: { stores } }) {
      return (signed && stores?.length > 1) || false;
    },
    multipleStores({ account: { signed }, user: { stores } }) {
      return signed && stores?.length > 1 ? stores : [];
    },
    canSwitchRole({ account: { signed }, user: { currentStore } }) {
      return (signed && currentStore.isManager) || false;
    },
    canSwitchStore(
      { account: { signed } },
      { isMultiStores, isSalerAccount, isStoreManagerAccount }
    ) {
      return (
        (signed &&
          isMultiStores &&
          (isSalerAccount || isStoreManagerAccount)) ||
        false
      );
    },
    isSalerAccount(state) {
      return (
        state.account.signed && state.account.role === USER_ROLES.CLIENT_AGENT
      );
    },
    isStoreManagerAccount(state) {
      return (
        state.account.signed && state.account.role === USER_ROLES.STORE_MANAGER
      );
    },
    isAreaManagerAccount(state) {
      return (
        state.account.signed && state.account.role === USER_ROLES.REGION_MANAGER
      );
    },
    isCustomerAccount(state) {
      return state.account.signed && state.account.role === ROLES.CUSTOMER;
    },
  },

  mutations: {
    updateReadyState(state) {
      state.readyState += 1;
    },
    setBrandInfo(state, brandInfo) {
      state.brandInfo = {
        ...brandInfo,
        brand: undefined,
        ...(brandInfo?.brand || {}),
        image: brandInfo?.brand?.headImage,
        logo: brandInfo?.brand?.logo,
        shareImage: brandInfo?.brand?.shareImage,
      };
    },
    setAccount(state, role) {
      state.account = {
        signed: true,
        role,
      };
    },
    setUser(state, user) {
      if (user?.caPosition) {
        state.user = {
          ...state.user,
          ...user,
        };
      } else {
        state.user = {};
      }
    },
    setCustomerInfo(state, info) {
      if (info.customerSearchVOS && info.customerSearchVOS.length > 0) {
        info.nickname = `${info.customerSearchVOS[0].lastName} ${info.customerSearchVOS[0].firstName}`;
        info.isTrueName = true;
      }
      state.customerInfo = info;
    },
    setCustomerDetail(state, detail) {
      state.customerDetail = detail;
    },
    setLogisticsOrderDetail(state, detail) {
      state.logisticsOrderDetail = detail;
    },
    clearCustomerInfo(state) {
      state.customerInfo = {};
    },
  },
  actions: {
    getBrandConfig({ commit }) {
      return getBrandConfigApi().then((data) => {
        commit("setBrandInfo", data);
        return data;
      });
    },
    async initUserInfo({ commit, state }, forceUpdate = false) {
      commit("setCustomerInfo", {});
      if (!forceUpdate && state.user?.caPosition) {
        return commit("setAccount", state.user.caPosition);
      }

      try {
        const res = await wxWorkGetUserInfoApi();
        console.log("getUserInfo res", res);
        // if (config.isDevelopment) {
        //   res.caPosition = USER_ROLES.CLIENT_AGENT;
        // }
        commit("setAccount", res.caPosition);
        commit("setUser", res);
      } catch (error) {
        commit("setAccount", ROLES.GUEST);
        commit("setUser", {});
        throw error;
      }
    },
    switchRoleAndStore({ commit }, payload) {
      Toast.loading({
        message: "",
        forbidClick: true,
      });
      return wxWorkSwitchUserInfoApi(payload)
        .then((res) => {
          commit("setAccount", res.caPosition);
          commit("setUser", res);
          Toast.clear();
          return true;
        })
        .catch((error) => {
          if (error?.message) Toast(error.message);
          else Toast.clear();
          return false;
        });
    },
    async switchSMRole({ getters, dispatch }, payload) {
      console.log("switchSMRole", getters.canSwitchRole, payload);
      if (getters.canSwitchRole) {
        const userRole =
          payload === USER_ROLES.STORE_MANAGER
            ? USER_ROLES.STORE_MANAGER
            : USER_ROLES.CLIENT_AGENT;
        // state.account.role = state.user.caPosition = userRole;
        // return true;
        return dispatch("switchRoleAndStore", {
          userRole,
          storeId: getters.currentStore.storeId,
        });
      }
      return false;
    },
    async switchCurrentStore({ state, getters, dispatch }, payload) {
      console.log("switchCurrentStore", getters.canSwitchRole, payload);
      if (getters.canSwitchStore) {
        const userRole = payload.isManager
          ? state.account.role
          : USER_ROLES.CLIENT_AGENT;
        // state.account.role = state.user.caPosition = userRole;
        // state.user.currentStore = payload;
        // return true;
        return dispatch("switchRoleAndStore", {
          userRole,
          storeId: payload.storeId,
        });
      }
      return false;
    },
    async initCustomerInfo({ commit }) {
      commit("setUser", {});
      try {
        const res = await getCustomerInfoApi();
        commit("setAccount", ROLES.CUSTOMER);
        commit("setCustomerInfo", res);
        if (isPureWeChat && !config.isDevelopAsInWeChat) {
          await useDefaultWeChatConfig();
        }
      } catch (error) {
        commit("setAccount", ROLES.GUEST);
        commit("setCustomerInfo", {});
        throw error;
      }
    },
    getCustomerInfo({ commit }, param) {
      return getCustomerInfoByExternalUserId(param.userId).then((res) => {
        commit("setCustomerInfo", res);
      });
    },
  },
});

// const { promise, resolve } = Promise.withResolvers();
let resolve;
let reject;
const promise = new Promise((res, rej) => {
  resolve = res;
  reject = rej;
});

store.accountReady = () => {
  const { state, getters, commit, dispatch } = store;
  if (state.readyState) return promise;
  commit("updateReadyState");
  console.log("[ Init Account ]");

  setTimeout(async () => {
    try {
      if (getters.runInWxWork) {
        console.log("init: isWechatWork");
        dispatch("getBrandConfig");
        await dispatch("initUserInfo", true);
      } else if (getters.runInWeChat) {
        console.log("init: isWechat");
        dispatch("getBrandConfig");
        await dispatch("initCustomerInfo", true);
      } else {
        if (config.isDevelopment) {
          dispatch("getBrandConfig");
          await dispatch("initUserInfo", true);
        }
        Toast("请在微信环境打开!");
      }
      console.log("store is ready");
      commit("updateReadyState");
      getEnums().finally(resolve);
    } catch (error) {
      console.log("Account Init Error", error.message);
      reject();
    }
  }, 0);
  return promise;
};

export default store;
